import React, { createContext, useContext, useState, useEffect } from 'react';

const ModalContext = createContext(null);

export const ModalProvider = ({ children }) => {
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [signupSource, setSignupSource] = useState(null);
  const [loginSource, setLoginSource] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [returnUrl, setReturnUrl] = useState(
    typeof window !== 'undefined' ? sessionStorage.getItem('loginReturnUrl') : null
  );

  // Initialize on mount - check URL params for ?modal=signup or ?modal=login
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const modalParam = params.get('modal');

      if (modalParam === 'signup') {
        setIsSignupOpen(true);
        setSignupSource('url-redirect');
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (modalParam === 'login') {
        setIsLoginOpen(true);
        setLoginSource('url-redirect');
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }

    setIsInitialized(true);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (isSignupOpen || isLoginOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isSignupOpen, isLoginOpen]);

  // Track modal views for analytics
  useEffect(() => {
    if (isSignupOpen && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'view_signup_modal', {
        source: signupSource || 'unknown'
      });
    }
  }, [isSignupOpen, signupSource]);

  useEffect(() => {
    if (isLoginOpen && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'view_login_modal', {
        source: loginSource || 'unknown'
      });
    }
  }, [isLoginOpen, loginSource]);

  const openSignup = (source = 'direct') => {
    setSignupSource(source);
    setIsSignupOpen(true);
    setIsLoginOpen(false); // Close login if it was open

    // Focus management for accessibility
    if (typeof document !== 'undefined') {
      document.body.focus();
    }
  };

  const openLogin = (source = 'direct', url = null) => {
    setLoginSource(source);
    setIsLoginOpen(true);
    setIsSignupOpen(false); // Close signup if it was open
    if (url) {
      setReturnUrl(url);
      if (typeof window !== 'undefined') sessionStorage.setItem('loginReturnUrl', url);
    }

    // Focus management for accessibility
    if (typeof document !== 'undefined') {
      document.body.focus();
    }
  };

  const clearReturnUrl = () => {
    setReturnUrl(null);
    if (typeof window !== 'undefined') sessionStorage.removeItem('loginReturnUrl');
  };

  const closeModal = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(false);
    setSignupSource(null);
    setLoginSource(null);
  };

  const switchToLogin = (source = 'modal-switch') => {
    // When user clicks "Inicia sesión" from signup modal
    setIsSignupOpen(false);
    setSignupSource(null);
    openLogin(source);
  };

  const switchToSignup = (source = 'modal-switch') => {
    // When user clicks "Empieza tu prueba" from login modal
    setIsLoginOpen(false);
    setLoginSource(null);
    openSignup(source);
  };

  const value = {
    isSignupOpen,
    isLoginOpen,
    signupSource,
    loginSource,
    openSignup,
    openLogin,
    closeModal,
    switchToLogin,
    switchToSignup,
    isInitialized,
    returnUrl,
    clearReturnUrl
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
